import { AxiosRequestConfig } from 'axios';
import { useEffect, useRef } from 'react';
import { authService, requestAxios } from 'services';
import { useAuth } from 'hooks';
import { messages } from 'helpers';

type CustomRequestConfig = AxiosRequestConfig & {
  _retry: boolean;
};

interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: CustomRequestConfig;
}

const refreshAndRetryQueue: RetryQueueItem[] = [];

let isRefreshing = false;

export function ResponseInterceptor() {
  const { signOut } = useAuth();

  const interceptorId = useRef<number | null>(null);

  useEffect(() => {
    interceptorId.current = requestAxios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { response } = error;

        const originalRequest: CustomRequestConfig = error.config;

        if (response && response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (!isRefreshing) {
            isRefreshing = true;

            try {
              const newAccessToken = await authService.refreshToken(error);

              error.config.headers['Authorization'] =
                `Bearer ${newAccessToken}`;

              refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
                requestAxios
                  .request(config)
                  .then((response) => resolve(response))
                  .catch((err) => reject(err));
              });

              refreshAndRetryQueue.length = 0;

              return requestAxios(originalRequest);
            } catch (refreshError) {
              signOut();

              return messages([{ errorCode: 'FRONT-RE-REFRESH_LOG_FAILED' }]);
            } finally {
              isRefreshing = false;
            }
          }

          return new Promise<void>((resolve, reject) => {
            refreshAndRetryQueue.push({
              config: originalRequest,
              resolve,
              reject,
            });
          });
        }

        messages(response?.data?.errors);

        return Promise.reject(error).catch((error) => error.response);
      }
    );

    return () => {
      requestAxios.interceptors.response.eject(interceptorId.current as number);
    };
  }, [signOut]);

  return null;
}
